import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { AppDispatch, RootState } from '../store/store';
import { fetchNotifications, markNotificationAsRead, fetchUnreadCount } from '../features/notifications/notificationsSlice';
import { selectCurrentUser } from '../features/auth/authSlice';
import { QMS_RECLAMOS_UI_ENABLED } from '../features/qms/qmsFeatures';

export function NotificationBell() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const { notifications, unreadCount, loading } = useSelector((state: RootState) => state.notifications);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (user) {
      dispatch(fetchNotifications());
      dispatch(fetchUnreadCount());
    }
  }, [dispatch, user]);

  // Polling cada 30 segundos
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      dispatch(fetchUnreadCount());
      if (isOpen) {
        dispatch(fetchNotifications());
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch, user, isOpen]);

  const handleNotificationClick = async (
    id: string,
    notificationType: string,
    relatedId?: string | null
  ) => {
    await dispatch(markNotificationAsRead(id));
    // Cerrar el dropdown
    setIsOpen(false);
    // Redirigir según el tipo de notificación
    if (notificationType === 'communication_matrix') {
      navigate('/communication-matrix');
      return;
    }
    if (relatedId && notificationType.startsWith('qms_non_conformity_')) {
      navigate(`/registros/no-conformidades/${relatedId}/planes`);
      return;
    }
    if (relatedId && notificationType.startsWith('qms_claim_')) {
      if (QMS_RECLAMOS_UI_ENABLED) {
        navigate(`/registros/reclamos/${relatedId}/planes`);
      } else {
        navigate('/registros/no-conformidades');
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} h`;
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  if (!user) return null;

  return (
    <div className="relative">
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            dispatch(fetchNotifications());
          }
        }}
        className="relative p-2 rounded-md text-foreground hover:bg-accent hover:text-accent-foreground transition-colors focus:outline-none"
        aria-label="Notificaciones"
      >
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)}></div>
          <div className="absolute right-0 mt-2 w-80 bg-card rounded-lg shadow-lg border border-border z-50 top-full max-h-96 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-border bg-muted">
              <h3 className="text-sm font-semibold text-foreground">Notificaciones</h3>
            </div>
            <div className="overflow-y-auto flex-1">
              {loading && notifications.length === 0 && (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  Cargando...
                </div>
              )}
              {!loading && notifications.length === 0 && (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No hay notificaciones
                </div>
              )}
              {notifications.length > 0 && (
                <div className="divide-y divide-border">
                  {notifications.map((notification) => (
                    <button
                      key={notification.id}
                      onClick={() =>
                        handleNotificationClick(notification.id, notification.type, notification.relatedId)
                      }
                      className="w-full text-left p-4 hover:bg-accent transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">
                            {notification.title}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {formatDate(notification.createdAt)}
                          </p>
                        </div>
                        {!notification.read && (
                          <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1"></div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>
        </>
      )}
    </div>
  );
}

